import { isPostPublishInFlight } from '../../utils/postPublishErrors';

const __POSTS_DEBUG__ = process.env.REACT_APP_DEBUG === 'true';

export function postsDebug(...args) {
  if (__POSTS_DEBUG__) {
    console.log(...args);
  }
}

/** Normalize media_urls field (string or legacy array) to a list of URL strings. */
export function parseMediaUrlsField(value) {
  if (value == null || value === '') return [];
  if (Array.isArray(value)) return value.map((u) => String(u).trim()).filter(Boolean);
  return String(value)
    .split(',')
    .map((u) => u.trim())
    .filter(Boolean);
}

/** Parse API date string to ms; null if missing/invalid. */
export function parsePostTimeMs(raw) {
  if (raw == null || raw === '') return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

/** Compare two timestamps; nulls sort last. direction asc = early→late. */
export function comparePostDates(aMs, bMs, direction) {
  if (aMs == null && bMs == null) return 0;
  if (aMs == null) return 1;
  if (bMs == null) return -1;
  const diff = aMs - bMs;
  return direction === 'asc' ? diff : -diff;
}

export function formatToEST(utcISOString) {
  if (!utcISOString) return 'N/A';
  const date = new Date(
    utcISOString.endsWith('Z') ? utcISOString : utcISOString + 'Z',
  );
  const options = {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  };
  return new Intl.DateTimeFormat('en-US', options)
    .format(date)
    .replace(',', '');
}

export function toMetricNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

export function getEngagementMetrics(post) {
  const metrics = post?.engagement_metrics || {};
  const readMetric = (...keys) => {
    for (const key of keys) {
      const value = toMetricNumber(metrics[key] ?? post?.[key]);
      if (value !== null) return value;
    }
    return null;
  };

  return {
    likes: readMetric('likes', 'likes_count'),
    comments: readMetric('comments', 'comments_count'),
    shares: readMetric('shares', 'shares_count'),
    reach: readMetric('reach', 'reach_count', 'impressions'),
    unavailable: Boolean(metrics.metrics_not_available_via_api),
    unavailableReason: metrics.reason || null,
  };
}

/** Phase 16.2: honest copy when metrics cannot be fetched. */
export function unavailableMetricsLabel(metricsOrReason) {
  const reason =
    typeof metricsOrReason === 'string'
      ? metricsOrReason
      : metricsOrReason?.unavailableReason ||
        metricsOrReason?.reason ||
        '';
  const r = String(reason).toLowerCase();
  if (r === 'api_permission' || r.includes('permission') || r.includes('limitation')) {
    return 'engagement unavailable (API limitation)';
  }
  if (r === 'quota' || r.includes('quota')) {
    return 'Engagement not synced (quota)';
  }
  return 'Metrics unavailable';
}

export function isPublishedPost(post) {
  const status = (post?.status || '').toLowerCase();
  const publishStatus = (post?.publish_status || '').toLowerCase();
  return status === 'published' || publishStatus === 'published';
}

export function hasSyncedEngagement(post) {
  const metrics = getEngagementMetrics(post);
  return ['likes', 'comments', 'shares', 'reach'].some(
    (key) => metrics[key] !== null,
  );
}

export function formatMetric(value) {
  return value === null ? '—' : value.toLocaleString();
}

export function getPublishStatusMeta(post) {
  const publishStatus = (post?.publish_status || '').toLowerCase();
  const status = (post?.status || '').toLowerCase();

  if (status === 'failed' || publishStatus === 'failed') {
    return { label: 'Failed', color: 'error', variant: 'filled' };
  }
  if (status === 'published' || publishStatus === 'published') {
    return { label: 'Published', color: 'success', variant: 'filled' };
  }
  if (isPostPublishInFlight(post)) {
    return { label: 'Publishing…', color: 'info', variant: 'outlined' };
  }
  if (publishStatus === 'queued' || status === 'scheduled') {
    return { label: 'Queued', color: 'warning', variant: 'outlined' };
  }
  if (status === 'draft') {
    return { label: 'Draft', color: 'default', variant: 'outlined' };
  }

  return {
    label: publishStatus || status || 'Unknown',
    color: 'default',
    variant: 'outlined',
  };
}
